import { Calendar, CalendarProps, DateLocalizer, dayjsLocalizer, Views, HeaderProps } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './BaseCalendar.scss';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useState, useMemo } from 'react';

export type BaseCalendarProps = Omit<CalendarProps, 'localizer'> & {
  localizer?: DateLocalizer;
  resources?: Array<{ id: string | number; name: string; color?: string; [key: string]: any }>;
  resourceIdAccessor?: string;
  resourceTitleAccessor?: string;
};

const localizer = dayjsLocalizer(dayjs);

const WeekHeader = ({ date, localizer }: HeaderProps) => {
  const dayName = localizer.format(date, 'ddd');
  const dayNumber = localizer.format(date, 'D');

  return (
    <div className="rbc-week-header-custom">
      <div className="rbc-week-header-day">{dayName}</div>
      <div className="rbc-week-header-date">{dayNumber}</div>
    </div>
  );
};

const MonthHeader = ({ date, localizer }: HeaderProps) => {
  const dayName = localizer.format(date, 'ddd');

  return (
    <div className="rbc-month-header-custom">
      {dayName}
    </div>
  );
};

const BaseCalendar = (props: BaseCalendarProps) => {
  const {
    views = {
      month: true,
      week: true,
      day: true,
    },
    formats = {
      dateFormat: 'D',
      weekdayFormat: 'dddd',
      dayFormat: 'dddd',
      timeGutterFormat: 'h A',
    },
    components: propsComponents,
    resources,
    resourceIdAccessor = 'id',
    resourceTitleAccessor = 'name',
    ...restProps
  } = props;

  const [view, setView] = useState(restProps.view || Views.MONTH);
  const [date, setDate] = useState(restProps.date || dayjs().toDate());

  const onView = useCallback((newView: any) => setView(newView), [setView]);
  const onNavigate = useCallback((newDate: any) => setDate(newDate), [setDate]);

  const components = useMemo(() => {
    const baseComponents = propsComponents || {};

    // If a custom header is already provided in props, use it
    if (baseComponents.header) {
      return baseComponents;
    }

    if (view === Views.WEEK) {
      return {
        ...baseComponents,
        header: WeekHeader,
      };
    }
    if (view === Views.MONTH) {
      return {
        ...baseComponents,
        header: MonthHeader,
      };
    }
    return baseComponents;
  }, [view, propsComponents]);

  useEffect(() => {
    if (props.view) setView(props.view);
  }, [props.view]);

  useEffect(() => {
    if (props.date) setDate(props.date);
  }, [props.date]);

  const calendarProps: any = {
    localizer,
    startAccessor: 'start',
    endAccessor: 'end',
    style: { height: '100%' },
    views,
    view,
    onView,
    date,
    onNavigate,
    formats,
    dayLayoutAlgorithm: view === 'day' ? 'no-overlap' : 'overlap',
    components,
    ...restProps,
  };

  if (resources) {
    calendarProps.resources = resources;
    calendarProps.resourceIdAccessor = resourceIdAccessor;
    calendarProps.resourceTitleAccessor = resourceTitleAccessor;
  }

  return (
    <>
      <Calendar {...calendarProps} />
    </>
  );
};

export default memo(BaseCalendar);
